<template>
  <div
    v-click-outside="closeDropdown"
    class="relative">
    <input
      ref="input"
      v-model="query"
      type="text"
      class="block w-full min-w-0 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
      :class="[{'pl-8': icon && selected !== null && !focused}, {'cursor-not-allowed bg-gray-100 text-gray-200': disabled}]"
      autocomplete="off"
      :placeholder="placeholder"
      :disabled="disabled"
      @focus="onFocus"
      @blur="onBlur"
      @keydown.enter.prevent
      @keydown.esc="closeDropdown">

    <span
      v-if="icon && selected !== null && !focused"
      class="absolute left-2 top-2.5 flex shrink-0 items-center justify-center text-success-dark">
      <slot
        name="selected-icon"
        :selected="selected">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="3"
          stroke="currentColor"
          class="h-5 w-5">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      </slot>
    </span>

    <span
      v-if="selected !== null && !focused"
      class="absolute right-3 top-3 cursor-pointer"
      @click="unselect">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="2"
        stroke="currentColor"
        class="h-4 w-4 text-gray-400">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M6 18L18 6M6 6l12 12" />
      </svg>
    </span>
    <ul
      v-if="open"
      class="absolute top-10 z-50 block w-full overflow-hidden rounded-lg border border-gray-300 bg-white text-gray-600">
      <template v-if="searchable">
        <li
          v-if="loading"
          class="cursor-disabled relative select-none">
          <svg
            class="dark:text-gray fill-blue m-2 h-4 w-4 animate-spin text-gray-200"
            viewBox="0 0 100 101"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor" />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill" />
          </svg>
        </li>
        <li
          v-else-if="(query.length > 0 || initialElements) && elements.length === 0"
          class="px-3 py-2 text-sm italic text-gray-400">
          <slot name="no-results">
            No results found.
          </slot>
        </li>
        <li
          v-else-if="query.length === 0"
          class="px-3 py-2 text-sm italic text-gray-400">
          <slot name="type-to-search">
            Start typing to search.
          </slot>
        </li>
      </template>
      <template v-if="!loading">
        <li
          v-for="element in elements"
          :key="element[identifier]"
          class="group relative flex cursor-pointer px-3 py-2 pl-8 hover:bg-indigo-500 hover:text-white"
          @click="select(element)">
          <span
            v-if="selected !== null && selected[identifier] === element[identifier]"
            class="absolute left-2 top-2.5 flex shrink-0 items-center justify-center text-success-dark group-hover:text-white">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="3"
              stroke="currentColor"
              class="h-5 w-5">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M4.5 12.75l6 6 9-13.5" />
            </svg>

          </span>
          <p>{{ element[attribute] }}</p>
        </li>

        <li
          v-if="modal"
          class="relative flex cursor-pointer bg-yellow-500 px-3 py-2 pl-8 text-sm italic text-white hover:bg-yellow-300"
          @click="openModal">
          <span
            class="absolute left-2 top-2.5 flex shrink-0 items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="h-4 w-4">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
          </span>
          <p>Create new</p>
        </li>
      </template>
    </ul>
    <slot
      name="modal"
      :show-modal="showModal"
      :query="query"
      :close-modal="closeModal"
      :select="select">
      <SmallModal
        :show="showModal"
        @close="closeModal">
        <div class="sm:flex sm:items-start">
          <div class="mt-3 text-center sm:mt-0 sm:text-left">
            <h3 class="text-lg font-medium leading-6 text-gray-900">
              Modal not implemented
            </h3>
          </div>
        </div>

        <div class="my-8 flex w-full flex-col space-y-4">
          Initial value: {{ query }}
        </div>

        <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            class="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
            @click="closeModal">
            Cancel
          </button>
        </div>
      </SmallModal>
    </slot>
  </div>
</template>

<script setup>
import {
  onMounted, ref, watch,
} from 'vue';
import SmallModal from '../Modal/SmallModal.vue';

const open = ref(false);
const focused = ref(false);
const loading = ref(false);
const selected = ref(null);
const elements = ref([]);
const initial = ref([]);
const query = ref('');
const input = ref();
const showModal = ref(false);
let timer;
let queryTimeout = null;

const emit = defineEmits(['focus', 'blur', 'closed', 'update:modelValue']);

const props = defineProps({
  attribute: {
    type: String,
    default: 'name',
  },
  identifier: {
    type: String,
    default: 'id',
  },
  placeholder: {
    type: String,
    default: 'Start typing to search',
  },
  searchUrl: {
    type: String,
    default: null,
  },
  initialUrl: {
    type: String,
    default: null,
  },
  items: {
    type: Array,
    default: [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  modal: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: Boolean,
    default: true,
  },
  modelValue: {
    type: Object,
    default: null,
  },
});

/**
 *
 */
function searchable() {
  return props.searchUrl.length > 0;
}

/**
 *
 */
function initialElements() {
  return props.initialUrl.length > 0;
}

/**
 *
 */
function resetElements() {
  if (initialElements()) {
    elements.value = initial.value;
  }
}

/**
 *
 */
function fetchInitialResults() {
  if (initialElements() && !props.disabled) {
    loading.value = true;
    axios
      .get(props.initialUrl)
      .then((response) => {
        initial.value = response.data.splice(0, 10);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        resetElements();
        loading.value = false;
      });
  } else if (props.items.length > 0) {
    elements.value = props.items;
  }
}

/**
 *
 */
function onFocus() {
  open.value = true;
  focused.value = true;
  emit('focus');
}

/**
 *
 */
function onBlur() {
  focused.value = false;
  emit('blur');
}

/**
 *
 */
function closeDropdown() {
  emit('closed');
  open.value = false;
  if (selected.value) {
    query.value = selected.value[props.attribute];
  }
}

/**
 *
 * @param value
 */
function select(value) {
  selected.value = value;
  query.value = value[props.attribute];
  emit('update:modelValue', selected.value);
  closeDropdown();
}

/**
 *
 */
function unselect() {
  selected.value = null;
  query.value = '';
  emit('update:modelValue', selected.value);
  closeDropdown();
}

/**
 *
 */
function search() {
  loading.value = true;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  timer = setTimeout(() => {
    axios
      .get(props.searchUrl + query.value)
      .then((response) => {
        elements.value = response.data.splice(0, 10);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        loading.value = false;
      });
  }, 500);
}

/**
 *
 */
function openModal() {
  showModal.value = true;
}

/**
 *
 */
function closeModal() {
  showModal.value = false;
}

onMounted(() => {
  if (props.modelValue) {
    selected.value = props.modelValue;
    query.value = props.modelValue[props.attribute];
  }
  fetchInitialResults();
});

watch(query, () => {
  // Clear the previous timeout if it exists
  if (queryTimeout) {
    clearTimeout(queryTimeout);
  }

  // Set a new timeout
  queryTimeout = setTimeout(() => {
    if (query.value.length === 0 && initialElements()) {
      resetElements();
    } else if (!selected.value || (selected.value && query.value !== selected.value[props.attribute])) {
      search();
    }
  }, 1000); // Adjust the delay as needed
});

watch(() => props.initialUrl, () => {
  unselect();
  fetchInitialResults();
});

</script>
